import React, { useCallback, useEffect, useState } from "react";

import { InfoSmIcon, Modal, Typography } from "@packages/ds";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import { getOrganizationProjects } from "Api/organizations/projects/getOrganizationProjects.request";
import Error from "Components/Error";
import PageMeta from "Components/PageMeta";
import config from "console_config";
import RegionPicker from "Containers/RegionPicker";
import Button, { IconControl, Link, ButtonWrapper } from "ds/Button";
import Dialog from "ds/Dialog";
import useInput from "Hooks/useInput";
import useQueryStrings from "Hooks/useQueryStrings";
import { selectCurrentUserUUID } from "Reducers/app/selectors";
import { selectFeatureOrganizationsEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { useOrganizationProfile } from "Reducers/organization/hooks/useOrganizationProfile";
import {
  canCreateProjectOrganizationsSelector,
  createOrganization,
  organizationByDescriptionIdSelector,
  organization_ErrorSelector,
  organizationInflightSelector,
  CreateOrganizationNamespace
} from "Reducers/organization/organization";
import { getOrganizationProfile } from "Reducers/organization/profile";
import { selectUserProfileByMeUsername } from "Reducers/profile";
import {
  setupCatalogLoadingSelector,
  setupCatalogSelector
} from "Reducers/setupCatalog/setupCatalog.selectors";
import { getSetupCatalogThunk } from "Reducers/setupCatalog/thunks/getSetupCatalog.thunk";
import { useLoadSetupOptions } from "Reducers/setupOptions/hooks/useLoadSetupOptions";
import { useSaveSetupOptionsConfig } from "Reducers/setupOptions/hooks/useSaveSetupOptionsConfig";
import { setupOptionsConfigSelector } from "Reducers/setupOptions/setupOptions.selectors";
import Sumup from "src/organization/pages/setup/components/Sumup";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import findTemplate from "./findTemplate";
import * as S from "./Info.styles";

const InfoStep = ({ goToStep, organizationNameId }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const saveSetupOptionsConfig = useSaveSetupOptionsConfig();
  const intl = useIntl();
  const { search } = useLocation();

  const params = new URLSearchParams(search);

  const [modalIsOpen, setOpenModal] = useState(false);
  const [template, setTemplate] = useState();
  const [projects, setProjects] = useState();

  const userUuid = useAppSelector(selectCurrentUserUUID);
  const organizationEnabled = useAppSelector(selectFeatureOrganizationsEnabled);

  const setupConfig = useAppSelector(setupOptionsConfigSelector);
  const profile = useAppSelector(selectUserProfileByMeUsername);
  const organizations = useAppSelector(canCreateProjectOrganizationsSelector);

  const { catalog: catalogUrl } = useQueryStrings(["catalog"]);
  useEffect(() => {
    if (templates.length === 0) dispatch(getSetupCatalogThunk({ catalogUrl }));
  }, [dispatch, getSetupCatalogThunk]);

  const getDefaultProjectName = useCallback(
    (organizationId, i = 1) => {
      const baseName = template ? template.name : "Custom project";
      const numberName = projects ? i : "";
      const proposedName = `${baseName} ${numberName}`;

      const nameTaken = projects
        ?.filter(project => project.organization_id === organizationId)
        .some(project => project.title === proposedName);

      if (nameTaken) {
        return getDefaultProjectName(organizationId, i + 1);
      }

      return proposedName;
    },
    [projects, template]
  );

  const newOrgError = useAppSelector(state =>
    organization_ErrorSelector(state, {
      organizationId: CreateOrganizationNamespace
    })
  );

  const newOrgErrorDetail = newOrgError?.detail;
  const createOrganizationError = newOrgError?.message;

  const validationFailed =
    "Validation failed: name must be between 3-39 characters long and can only contain lowercase alphanumeric characters and non-consecutive dashes.";
  const isOrgPathError = newOrgErrorDetail === validationFailed;
  const newOrganizationError = isOrgPathError
    ? validationFailed
    : newOrgErrorDetail;

  const isCreateOrganizationLoading = useAppSelector(state =>
    organizationInflightSelector(state, {
      organizationId: CreateOrganizationNamespace
    })
  );

  const hasOrganizations = organizations?.length > 0;
  const { inputs, handleInputChange, setInputs } = useInput({
    title: setupConfig?.title || "",
    defaultBranch:
      config.CUSTOM_DEFAULT_BRANCH || setupConfig?.defaultBranch?.main || "",
    region: setupConfig?.region,
    organization: hasOrganizations ? {} : ""
  });
  const orgaizationPathError = newOrgErrorDetail?.startsWith(
    "Organization name"
  )
    ? `Organization path ${inputs?.organization} already in use.`
    : newOrgErrorDetail;

  const organizationNameSelected = organizations?.find(
    o => o.id === inputs?.organization?.value
  )?.name;
  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationNameSelected
    })
  );

  useEffect(() => {
    async function fetchProject() {
      if (organization) {
        const projects = await getOrganizationProjects(organization.id);
        setProjects(projects?.items);
      }
    }
    fetchProject();
  }, [organization]);

  const { data: templates } = useAppSelector(setupCatalogSelector);
  const isSetupCatalogLoading = useAppSelector(setupCatalogLoadingSelector);

  const { organizationProfile, isLoading: isLoadingProfile } =
    useOrganizationProfile(organizationNameSelected);

  useEffect(() => {
    if (
      organizationEnabled &&
      organizationNameSelected &&
      organization &&
      !organizationProfile
    ) {
      !isLoadingProfile &&
        dispatch(
          getOrganizationProfile({
            organizationId: organizationNameSelected,
            navigate
          })
        );
    }
  }, [organizationNameSelected, organization]);

  const activeTrial =
    organizationEnabled && organizationProfile?.current_trial?.active;

  useLoadSetupOptions(organization?.id);

  useEffect(() => {
    const focusElement = document.getElementById("projectTitle");
    focusElement && focusElement.focus();
    // If the user have only one org, we hide the dropdown and we choose this one by default
    let orgData;
    if (organizationEnabled && organizations?.length === 1) {
      orgData = organizations[0];
    } else if (organizationEnabled) {
      orgData = organizations?.find(o => o.name === organizationNameId);
    }
    if (orgData) {
      const name = getDefaultProjectName(orgData.id);

      setInputs({
        ...inputs,
        title: name,
        organization: { label: orgData.label, value: orgData.id }
      });
    }
  }, [projects, template]);

  useEffect(() => {
    const getTemplate = () => {
      if (!params.has("template") && !setupConfig?.template) return;
      const templateUrl = params.get("template") || setupConfig?.template;

      const tpl = findTemplate(templateUrl, templates);

      if (!tpl) return goToStep("");

      setTemplate({
        image: tpl.image,
        name: tpl.name
      });
    };
    !isSetupCatalogLoading && getTemplate();
  }, [
    setupConfig?.template,
    params.has("template"),
    templates?.length,
    isSetupCatalogLoading
  ]);

  const handleCancel = () => {
    setOpenModal(false);
  };

  const handleBuild = () => {
    setOpenModal(false);
    goToStep("building");
  };

  const onRegionChange = region => {
    setInputs(inputs => ({ ...inputs, region }));
  };

  const canSubmit = () => {
    const { title, region, organization } = inputs;

    if (organizationEnabled && hasOrganizations && !organization?.value) {
      return false;
    }
    return title !== "" && region;
  };

  const handleGoBack = e => {
    e?.preventDefault();
    goToStep(setupConfig?.template ? "template" : "type");
  };

  const handleSubmit = async e => {
    e?.preventDefault();

    if (isCreateOrganizationLoading) return;

    let newConfig = inputs;

    if (!hasOrganizations) {
      const newOrganizationConfig = {
        name: inputs.organization,
        owner_id: userUuid,
        label: inputs.organization,
        country: profile?.country
      };

      const newOrganization = await createOrganization({
        organization: newOrganizationConfig
      })(dispatch);

      // if createOrganization failed, don't go to the next step and display the error.
      if (!newOrganization) return;

      newConfig = {
        ...inputs,
        organization: {
          value: newOrganization?.data.id,
          label: newOrganization?.data.label
        }
      };
    }
    saveSetupOptionsConfig({ ...newConfig });
    if (!organizationProfile?.current_trial?.active) {
      setOpenModal(true);
    } else {
      goToStep("building");
    }
  };
  const handleOrganizationCreate = organization => {
    setInputs({
      ...inputs,
      organization: organization.target.value
    });
  };

  return (
    <>
      {modalIsOpen && (
        <Modal
          isOpen={true}
          onOpenChange={handleCancel}
          handleClose={handleCancel}
          id="modal-plan-pricing"
          size="medium"
        >
          <Typography.Title tag="h2">
            {intl.formatMessage({ id: "plan_and_pricing" })}
          </Typography.Title>
          <Sumup activeTrial={activeTrial} />
          <ButtonWrapper spacing="modal" justifyContent="end">
            <Button
              variant="secondary"
              analyticId="cancel"
              onClick={handleCancel}
            >
              {intl.formatMessage({ id: "cancel" })}
            </Button>
            <Button
              id="delete-modal-plan-pricing-confirm-modal-button"
              onClick={handleBuild}
              analyticId="continue"
            >
              {intl.formatMessage({ id: "continue" })}
            </Button>
          </ButtonWrapper>
        </Modal>
      )}
      {createOrganizationError && <Error>{createOrganizationError}</Error>}

      {newOrgErrorDetail && !isOrgPathError && (
        <Error>{orgaizationPathError}</Error>
      )}

      <S.Wrapper>
        <PageMeta title={intl.formatMessage({ id: `setup.info.title` })} />

        <S.Header>
          {template?.image && (
            <img alt={`${template.name} logo`} src={template.image} />
          )}
          <S.Title tag="h2">
            {intl.formatMessage({ id: `setup.info.title` })}
          </S.Title>
          {template && (
            <p data-analytics-template={template.name}>{template.name}</p>
          )}
        </S.Header>

        <S.Form>
          <S.Input
            id="title"
            name="title"
            className="field"
            label={intl.formatMessage({ id: "project_name" })}
            onChange={handleInputChange}
            value={inputs.title}
            placeholder={intl.formatMessage({ id: "project_name" })}
            required
            width="100%"
          />

          <S.Input
            id="defaultBranch"
            name="defaultBranch"
            className="field"
            required={false}
            label={
              <S.DefaultBranchLabelContainer>
                {intl.formatMessage({ id: "production_environment_name" })}
                <Dialog
                  description={intl.formatMessage({
                    id: "production_environment_info"
                  })}
                  title={intl.formatMessage({
                    id: "production_environment"
                  })}
                  analyticId="production_environment_info"
                  trigger={
                    <IconControl aria-label="info-trigger-production_environment_info">
                      <InfoSmIcon />
                    </IconControl>
                  }
                  link={
                    <Link
                      href={intl.formatMessage({
                        id: "links.documentation.environments.default_environment"
                      })}
                      analyticId="production_environment_learn_more"
                    >
                      {intl.formatMessage({
                        id: "production_environment_learn_more"
                      })}
                    </Link>
                  }
                />
              </S.DefaultBranchLabelContainer>
            }
            placeholder={intl.formatMessage({
              id: "production_environment_name"
            })}
            onChange={handleInputChange}
            value={inputs.defaultBranch}
            width="100%"
          />

          <RegionPicker onChange={onRegionChange} region={inputs.region} />
          {!hasOrganizations && (
            <>
              <S.Input
                id="new_organization"
                name="new_organization"
                className="field"
                label={intl.formatMessage({ id: "organization_path" })}
                onChange={handleOrganizationCreate}
                value={inputs.organization}
                placeholder={intl.formatMessage({ id: "organization_path" })}
                error={isOrgPathError && newOrganizationError}
                width="100%"
                required
              />

              <S.NameInputPrefix>
                {`https://${window.location.host}/`}
                <S.NameInputPrefixValue>
                  {inputs.organization ||
                    intl.formatMessage({
                      id: "organization.url.preview.placeholder"
                    })}
                </S.NameInputPrefixValue>{" "}
              </S.NameInputPrefix>
            </>
          )}
        </S.Form>

        <S.ActionButtons>
          {!params.has("template") && (
            <Button
              variant="secondary"
              onClick={handleGoBack}
              disabled={isCreateOrganizationLoading}
              analyticId="back"
            >
              {intl.formatMessage({ id: "back" })}
            </Button>
          )}

          <Button
            type="submit"
            onClick={handleSubmit}
            analyticId="create_project"
            disabled={!canSubmit()}
          >
            {intl.formatMessage({ id: "create_project" })}
          </Button>
        </S.ActionButtons>
      </S.Wrapper>
    </>
  );
};

InfoStep.propTypes = {
  goToStep: PropTypes.func,
  organizationNameId: PropTypes.string
};

export default InfoStep;
